import { useCallback, useEffect, useRef, useState } from "react";

export interface UseInfiniteSentinelOptions {
  enabled: boolean;
  onIntersect: () => void;
  rootMargin?: string;
}

/**
 * Callback ref for a sentinel element below a list; calls `onIntersect` each time the sentinel
 * comes within `rootMargin` of the viewport while `enabled`. The observer is recreated whenever
 * `enabled` flips back on, so a sentinel that is still visible after a page loads fires again.
 */
export function useInfiniteSentinel({ enabled, onIntersect, rootMargin = "400px" }: UseInfiniteSentinelOptions) {
  const [node, setNode] = useState<Element | null>(null);
  const onIntersectRef = useRef(onIntersect);
  onIntersectRef.current = onIntersect;

  useEffect(() => {
    if (!enabled || !node || typeof IntersectionObserver === "undefined") return;

    const observer = new IntersectionObserver(
      entries => {
        if (entries.some(entry => entry.isIntersecting)) onIntersectRef.current();
      },
      { rootMargin }
    );
    observer.observe(node);
    return () => observer.disconnect();
  }, [enabled, node, rootMargin]);

  return useCallback((element: Element | null) => setNode(element), []);
}
